/*
 *  -----------------------------------------------------------------------
 *    <copyright file="field-selector.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import FormControl from '@mui/material/FormControl';
import { intersection } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { useLocalState, useTranslation, useLogger } from 'hooks';
import { RowDataSourceTable } from 'types/graphql-global-types';
import CustomMultiSelect from 'views/components/core/multi-select/multi-select';
import SelectWithCategories from 'views/components/core/select-with-categories';

interface IFieldSelector {
    dataSource: string;
    formats: string[];
    customId?: string;
    onChange?: any;
    label?: any;
    fieldSelect?: any;
    labelField?: any;
    error?: any;
    value: any;
    customClassName?: any;
    excludeValue?: Function;
    tags?: string[];
    isCalculated?: boolean;
    searchable?: boolean;
    multiselect?: boolean;
    includeParentFields?: boolean;
    disabled?: boolean;
    appendPopupToSelect?: boolean;
}

export const Formats = {
    Text: 'TEXT',
    Date: 'DATE',
    PartialDate: 'PARTIAL_DATE',
    Int: 'INT',
    Float: 'FLOAT',
    Bool: 'BOOL',
};

export const FieldSelector = ({
    dataSource,
    formats,
    customId,
    onChange,
    label,
    fieldSelect,
    labelField,
    error,
    value,
    customClassName,
    excludeValue,
    tags,
    isCalculated,
    searchable,
    multiselect,
    includeParentFields,
    disabled,
    appendPopupToSelect,
}: IFieldSelector) => {
    const { state: localState } = useLocalState();
    const Logger = useLogger();

    const source = localState?.dataSources?.find((s: any) => s.id === dataSource);
    let dataSourceTable = source?.table ?? dataSource;
    if (dataSourceTable === RowDataSourceTable.NONE) dataSourceTable = source.id;

    const isDataSourceMatch = (fieldType: string) => {
        if (fieldType === dataSourceTable) return true;

        if (!includeParentFields) return false;

        switch (dataSourceTable) {
            case RowDataSourceTable.SITE:
                return fieldType === RowDataSourceTable.STUDY;

            case RowDataSourceTable.SITE_ISSUE:
            case RowDataSourceTable.SITE_VISIT:
            case RowDataSourceTable.SITE_PROTOCOL_DEVIATION:
            case RowDataSourceTable.SUBJECT:
                return fieldType === RowDataSourceTable.SITE || fieldType === RowDataSourceTable.STUDY;

            case RowDataSourceTable.SUBJECT_ADVERSE_EVENT:
            case RowDataSourceTable.SUBJECT_ASSESSMENT:
            case RowDataSourceTable.SUBJECT_CRF:
            case RowDataSourceTable.SUBJECT_INFORMED_CONSENT:
            case RowDataSourceTable.SUBJECT_ISSUE:
            case RowDataSourceTable.SUBJECT_LAB:
            case RowDataSourceTable.SUBJECT_PROTOCOL_DEVIATION:
            case RowDataSourceTable.SUBJECT_QUERY:
            case RowDataSourceTable.SUBJECT_VISIT:
                return (
                    fieldType === RowDataSourceTable.SUBJECT ||
                    fieldType === RowDataSourceTable.SITE ||
                    fieldType === RowDataSourceTable.STUDY
                );

            default:
                return false;
        }
    };

    const items =
        localState?.fields?.filter(
            (field: any) =>
                !field.isHidden &&
                isDataSourceMatch(field.rowDataSource ?? field.dataType) &&
                (!formats?.length || formats.indexOf(field.fieldType)) >= 0 &&
                (isCalculated === undefined || isCalculated === field.isCalculated) &&
                (!excludeValue || excludeValue(field)) &&
                (!tags || intersection(field.tags, tags).length),
        ) ?? [];

    items.sort((a: any, b: any) => `${a.dataType}.${a.name}`.localeCompare(`${b.dataType}.${b.name}`));

    useEffect(() => {
        Logger.log(
            `Fields in dropdown '${label}': `,
            items.map((item: any) => `${item.dataType}.${item.name}`),
        );
    }, [dataSource]);

    const { t } = useTranslation();

    const getOptions = () => {
        const options = items.map((item: any) => ({
            id: item.key,
            text: `${item.dataType}.${item.name}`,
            ...item,
        }));
        if (!multiselect) options.unshift({ id: '', text: t('ra:selectValue'), active: true, default: true });
        return options;
    };

    const options = useMemo(getOptions, [items, multiselect]);

    return (
        <FormControl variant="outlined" className="form-control-select">
            {!multiselect ? (
                <SelectWithCategories
                    showWarning={false}
                    customClassName={customClassName}
                    onChange={() => (event: any) => {
                        onChange(event?.target?.value);
                    }}
                    label={label || ''}
                    filterable={false}
                    fieldSelect={fieldSelect}
                    labelField={labelField}
                    dataArray={options}
                    customId={customId || ''}
                    currentState={value}
                    error={error}
                    searchable={searchable}
                    disabled={disabled}
                    appendPopupToSelect={appendPopupToSelect}
                />
            ) : (
                <CustomMultiSelect
                    items={options}
                    onChange={onChange}
                    value={value}
                    label={label || ''}
                    customClassName={customClassName}
                    customId={customId || ''}
                    placeholder={t('enterSearchTerm')}
                    error={error}
                    disabled={disabled}
                />
            )}
        </FormControl>
    );
};
